import { Dialog, DialogContent } from 'components/ui/dialog';
import { Type, Layers, Medal, Crop } from 'lucide-react';
import { useState, useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';

type ChunkType = {
  id: string;
  title: string;
  url?: string;
  content: string;
  tokens: number;
  layers: number;
  score: number;
  tags: string[];
};

type ChunkDetailsModalProps = {
  chunk: ChunkType | null;
  onClose: () => void;
};

export const ChunkDetailsModal = ({ chunk, onClose }: ChunkDetailsModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentChunk, setCurrentChunk] = useState<ChunkType | null>(null);

  useEffect(() => {
    if (chunk) {
      setCurrentChunk(chunk);
      setIsOpen(true);
    }
  }, [chunk]);

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        if (!chunk) {
          setCurrentChunk(null);
        }
        onClose();
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, onClose, chunk]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
        {currentChunk && (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-3 border-b pb-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex cursor-help items-center gap-1.5 rounded-md bg-muted px-3 py-2">
                      <Crop className="h-5 w-5 text-muted-foreground" />
                      <span className="text-base font-medium">{currentChunk.id}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Section (A unique ID for this specific piece of text)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex cursor-help items-center gap-1.5 rounded-md bg-muted px-3 py-2">
                      <Type className="h-5 w-5 text-muted-foreground" />
                      <span className="text-base font-medium">{currentChunk.tokens}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Tokens (How much text is in this section)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex cursor-help items-center gap-1.5 rounded-md bg-muted px-3 py-2">
                      <Layers className="h-5 w-5 text-muted-foreground" />
                      <span className="text-base font-medium">{currentChunk.layers}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Depth (How deeply this text was analyzed)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex cursor-help items-center gap-1.5 rounded-md bg-muted px-3 py-2">
                      <Medal className="h-5 w-5 text-muted-foreground" />
                      <span className="text-base font-medium">{currentChunk.score}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Quality (How closely this matches what you searched for)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="space-y-4">
              {currentChunk.url && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold">URL Source:</h3>
                  <a
                    href={chunk?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-sm text-primary hover:underline"
                  >
                    {currentChunk.url}
                  </a>
                </div>
              )}
              <div>
                <h3 className="break-all text-sm font-bold">
                  [Chunk #{currentChunk?.id}, Title: {currentChunk?.title}]
                </h3>
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold">Content:</h3>
                <p
                  className="whitespace-pre-wrap break-all text-sm leading-relaxed"
                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {currentChunk.content}
                </p>
              </div>
              <div>
                <div className="flex flex-wrap gap-2">
                  {currentChunk.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="break-all rounded-md bg-muted px-3 py-1.5 text-sm text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
